/**
 * Backend BaseDataTemplate.TemplateTargetType values (uppercase strings in JSON).
 * Templates with no targetTypes are shown in every context (backward compatible).
 */

export const TEMPLATE_CONTEXT = {
  PROJECT: "PROJECT",
  COLLECTION: "COLLECTION",
  ENTITY: "ENTITY",
};

/** All valid contexts, in display order. */
export const ALL_TEMPLATE_CONTEXTS = [
  TEMPLATE_CONTEXT.PROJECT,
  TEMPLATE_CONTEXT.COLLECTION,
  TEMPLATE_CONTEXT.ENTITY,
];

/** User-facing labels for template scope (section titles and selector labels). */
export const TEMPLATE_SCOPE_LABELS = {
  [TEMPLATE_CONTEXT.PROJECT]: "Project Custom Data",
  [TEMPLATE_CONTEXT.COLLECTION]: "Collection Custom Data",
  [TEMPLATE_CONTEXT.ENTITY]: "Map Entity Custom Data",
};

export function getTemplateScopeLabel(context) {
  if (!context) return "Custom Data";
  return TEMPLATE_SCOPE_LABELS[String(context).toUpperCase()] || "Custom Data";
}

const VALID_TARGETS = new Set(Object.values(TEMPLATE_CONTEXT));

/**
 * @param {unknown} raw - from API (array of strings or undefined)
 * @returns {string[]} normalized uppercase target type names
 */
export const normalizeTargetTypes = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw
    .map((t) => String(t || "").trim().toUpperCase())
    .filter((t) => VALID_TARGETS.has(t));
};

/**
 * Editor default: empty/legacy (BE stores whatever we send; FE listing treats
 * empty as every context) shows as all three checked.
 */
export const targetTypesForCreateEditor = (raw) => {
  const normalized = normalizeTargetTypes(raw);
  return normalized.length > 0 ? normalized : [...ALL_TEMPLATE_CONTEXTS];
};

/**
 * @param {{ targetTypes?: string[] } | null | undefined} template - normalized template
 * @param {string} context - TEMPLATE_CONTEXT.*
 * @returns {boolean}
 */
export const isTemplateAllowedForContext = (template, context) => {
  if (!template || !context) return true;
  const targets = normalizeTargetTypes(template.targetTypes);
  if (targets.length === 0) return true;
  return targets.includes(String(context).toUpperCase());
};

/**
 * @param {Array<{ targetTypes?: string[] }>} templates
 * @param {string} context
 * @returns {Array}
 */
export const filterTemplatesByContext = (templates, context) => {
  if (!Array.isArray(templates)) return [];
  return templates.filter((t) => isTemplateAllowedForContext(t, context));
};

/**
 * Keeps the current selection visible in a filtered dropdown when the API list
 * omits it (e.g. scope filter) or the template was loaded from elsewhere.
 */
export const ensureSelectedTemplateVisible = (filteredList, selectedId, allTemplates) => {
  const list = Array.isArray(filteredList) ? filteredList : [];
  if (!selectedId || !Array.isArray(allTemplates)) return list;
  if (list.some((t) => t?.id === selectedId)) return list;
  const found = allTemplates.find((t) => t?.id === selectedId);
  return found ? [found, ...list] : list;
};
